import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import {
  completeTrip,
  fetchTrip,
  generateDispatchQr,
  startTrip,
  type DispatchTrip,
  type TripDispatchItem,
} from '@/features/delman/api/tripApi'
import { delmanTerms } from '@/features/delman/delmanTerms'
import {
  aggregateLineQtyByUom,
  isDispatchOpen,
  nextOpenDispatchId,
  tripProgress,
} from '@/features/delman/tripProgress'
import { formatQtyByUom } from '@/features/warehouse/stockGroups'
import { EmptyState } from '@/shared/components/EmptyState'
import { QrDisplay } from '@/shared/components/QrDisplay'
import { SkeletonList } from '@/shared/components/Skeleton'
import { Toast } from '@/shared/components/Toast'

type QrState = { token: string; payload: string; expiresAt: string }

function tripStatusBadge(status: string): string {
  if (status === 'in_progress') return 'badge-warning'
  if (status === 'completed') return 'badge-success'
  if (status === 'cancelled') return 'badge-danger'
  return 'badge-draft'
}

function tripStatusLabel(status: string): string {
  return delmanTerms.trip.status[status] ?? status
}

function dispatchStatusBadge(status: string): string {
  if (status === 'in_transit') return 'badge-warning'
  if (status === 'receiving_completed') return 'badge-success'
  if (status === 'cancelled') return 'badge-danger'
  return 'badge-draft'
}

function dispatchStatusLabel(status: string): string {
  return delmanTerms.trip.dispatchStatus[status] ?? status
}

function countdownLabel(expiresAt: string, now: number): { label: string; expired: boolean } {
  const remainingMs = new Date(expiresAt).getTime() - now
  if (remainingMs <= 0) return { label: delmanTerms.trip.qrExpired, expired: true }
  const totalSec = Math.floor(remainingMs / 1000)
  const mm = String(Math.floor(totalSec / 60)).padStart(2, '0')
  const ss = String(totalSec % 60).padStart(2, '0')
  return { label: delmanTerms.trip.qrExpiresIn(`${mm}:${ss}`), expired: false }
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    /* fall through */
  }
  try {
    const el = document.createElement('textarea')
    el.value = text
    el.setAttribute('readonly', '')
    el.style.position = 'fixed'
    el.style.left = '-9999px'
    document.body.appendChild(el)
    el.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(el)
    return ok
  } catch {
    return false
  }
}

function KitchenDispatchCard({
  dispatch,
  tripStatus,
  expanded,
  showAllMaterials,
  onToggle,
  onToggleMaterials,
  qr,
  now,
  onGenerateQr,
  isGenerating,
  onToast,
}: {
  dispatch: TripDispatchItem
  tripStatus: string
  expanded: boolean
  showAllMaterials: boolean
  onToggle: () => void
  onToggleMaterials: () => void
  qr?: QrState
  now: number
  onGenerateQr: (dispatchId: string) => void
  isGenerating: boolean
  onToast: (message: string) => void
}) {
  const [fullscreen, setFullscreen] = useState(false)
  const lines = dispatch.lines
  const previewLines = lines.slice(0, showAllMaterials ? lines.length : 3)
  const qtySummary = formatQtyByUom(aggregateLineQtyByUom(lines))
  const canShowQr =
    expanded &&
    tripStatus === 'in_progress' &&
    (dispatch.status === 'dispatched' || dispatch.status === 'in_transit')
  const countdown = qr ? countdownLabel(qr.expiresAt, now) : null
  const qrLive = Boolean(qr && countdown && !countdown.expired)
  const panelId = `dispatch-panel-${dispatch.id}`

  useEffect(() => {
    if (!fullscreen || !qrLive) {
      if (!qrLive) setFullscreen(false)
      return undefined
    }
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setFullscreen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [fullscreen, qrLive])

  async function handleCopyPayload() {
    if (!qr?.payload) return
    const ok = await copyText(qr.payload)
    onToast(ok ? delmanTerms.trip.copyPayloadSuccess : delmanTerms.trip.copyPayloadFailed)
  }

  return (
    <div className="card space-y-0 overflow-hidden">
      <button
        type="button"
        className="w-full text-left min-h-[44px] py-3 flex items-start justify-between gap-2"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
      >
        <span className="min-w-0 block">
          <span className="block text-body-lg font-semibold text-on-surface break-words">
            → {dispatch.target_kitchen_name}
          </span>
          <span className="block text-label-md font-mono text-on-surface-variant mt-1">{dispatch.code}</span>
          <span className="block text-body-sm text-on-surface-variant mt-1">
            {delmanTerms.trip.materialsCount(lines.length)}
          </span>
        </span>
        <span className={`shrink-0 ${dispatchStatusBadge(dispatch.status)}`}>
          {dispatchStatusLabel(dispatch.status)}
        </span>
      </button>

      {expanded && (
        <div id={panelId} role="region" className="space-y-3 border-t border-surface-variant pt-3 pb-1">
          <div className="space-y-1">
            {qtySummary ? (
              <p className="text-body-sm text-on-surface">{qtySummary}</p>
            ) : null}
            <p className="text-body-sm text-on-surface-variant">{delmanTerms.trip.materialsCount(lines.length)}</p>
          </div>

          {lines.length > 0 && (
            <ul className="text-body-sm text-on-surface-variant space-y-1">
              {previewLines.map((line) => (
                <li key={line.id}>{delmanTerms.trip.materialLine(line.material_name, line.uom, line.qty)}</li>
              ))}
            </ul>
          )}

          {lines.length > 3 && (
            <button type="button" className="text-body-sm text-primary min-h-[44px]" onClick={onToggleMaterials}>
              {showAllMaterials ? delmanTerms.trip.hideMaterials : delmanTerms.trip.showAllMaterials}
            </button>
          )}

          {canShowQr && (
            <div className="pt-2 border-t border-surface-variant space-y-3">
              {qrLive && qr && countdown && (
                <div className="flex flex-col items-center gap-2">
                  <QrDisplay token={qr.payload} size="large" />
                  <p className="text-body-sm text-on-surface-variant">{countdown.label}</p>
                  <p className="text-body-sm text-on-surface-variant text-center">{delmanTerms.trip.qrHint}</p>
                  <p className="font-mono text-label-md text-center break-all px-2 text-on-surface">{qr.payload}</p>
                  <p className="text-body-sm text-on-surface-variant text-center">{delmanTerms.trip.qrPayloadHint}</p>
                  <div className="flex w-full gap-2">
                    <button type="button" className="btn-outline flex-1 min-h-[44px]" onClick={() => void handleCopyPayload()}>
                      <span className="inline-flex items-center justify-center gap-2">
                        <span className="material-symbols-outlined text-[18px]">content_copy</span>
                        {delmanTerms.trip.copyPayload}
                      </span>
                    </button>
                    <button type="button" className="btn-outline flex-1 min-h-[44px]" onClick={() => setFullscreen(true)}>
                      <span className="inline-flex items-center justify-center gap-2">
                        <span className="material-symbols-outlined text-[18px]">fullscreen</span>
                        {delmanTerms.trip.fullscreen}
                      </span>
                    </button>
                  </div>
                </div>
              )}
              <button
                type="button"
                className="btn-outline w-full min-h-[44px]"
                disabled={isGenerating}
                onClick={() => onGenerateQr(dispatch.id)}
              >
                {isGenerating
                  ? delmanTerms.trip.generatingQr
                  : qrLive
                    ? delmanTerms.trip.regenerateQr
                    : delmanTerms.trip.generateQr}
              </button>
            </div>
          )}
        </div>
      )}

      {fullscreen && qrLive && qr && countdown && (
        <div
          className="fixed inset-0 z-50 bg-inverse-surface flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-label={delmanTerms.trip.fullscreenDialogAria}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="min-w-0">
              <p className="text-body-sm font-semibold text-inverse-on-surface break-words">
                → {dispatch.target_kitchen_name}
              </p>
              <p className="font-mono text-body-sm text-inverse-on-surface/60">{dispatch.code}</p>
            </div>
            <button
              type="button"
              className="shrink-0 min-h-[44px] min-w-[44px] rounded-lg text-inverse-on-surface/80 hover:text-inverse-on-surface flex items-center justify-center"
              onClick={() => setFullscreen(false)}
              aria-label={delmanTerms.trip.fullscreenClose}
            >
              <span className="material-symbols-outlined text-2xl">close</span>
            </button>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center p-6 gap-4 overflow-y-auto">
            <QrDisplay token={qr.payload} size="large" />
            <p className="text-body-sm text-inverse-on-surface/80">{countdown.label}</p>
            <p className="font-mono text-body-sm text-inverse-on-surface text-center break-all px-2">{qr.payload}</p>
            <p className="text-body-sm text-inverse-on-surface/70 text-center">{delmanTerms.trip.qrPayloadHint}</p>
            <button
              type="button"
              className="btn-outline min-h-[48px] !text-inverse-on-surface !border-white/40 !bg-white/10 px-6"
              onClick={() => void handleCopyPayload()}
            >
              <span className="inline-flex items-center justify-center gap-2">
                <span className="material-symbols-outlined">content_copy</span>
                {delmanTerms.trip.copyPayload}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/** GET/PATCH /warehouse/dispatch-trips/{tripId} — detail sesi kirim: start, QR per dispatch, complete. */
export function DelmanTripDetailPage() {
  const params = useParams<{ tripId: string }>()
  const tripId = params.tripId ?? ''
  const navigate = useNavigate()

  const [trip, setTrip] = useState<DispatchTrip | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [isStarting, setIsStarting] = useState(false)
  const [isCompleting, setIsCompleting] = useState(false)
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [qrByDispatch, setQrByDispatch] = useState<Record<string, QrState>>({})
  const [generatingDispatchId, setGeneratingDispatchId] = useState<string | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [showAllMaterials, setShowAllMaterials] = useState(false)

  const requestGen = useRef(0)
  const prevStatusByIdRef = useRef<Record<string, string>>({})
  const focusRefreshTimer = useRef<number | undefined>(undefined)

  const dispatchStatusSignature = trip
    ? trip.dispatches.map((d) => `${d.id}:${d.status}`).join('|')
    : ''

  const loadTrip = useCallback(
    (opts?: { silent?: boolean }) => {
      if (!tripId) return
      const silent = opts?.silent === true
      const gen = ++requestGen.current

      if (silent) {
        setIsRefreshing(true)
      } else {
        setIsLoading(true)
        setLoadError(false)
      }

      fetchTrip(tripId)
        .then((res) => {
          if (gen !== requestGen.current) return
          setTrip(res)
          if (!silent) setLoadError(false)
        })
        .catch(() => {
          if (gen !== requestGen.current) return
          if (!silent) setLoadError(true)
        })
        .finally(() => {
          if (gen !== requestGen.current) return
          setIsLoading(false)
          setIsRefreshing(false)
        })
    },
    [tripId],
  )

  useEffect(() => {
    prevStatusByIdRef.current = {}
    setExpandedId(null)
    loadTrip({ silent: false })
    return () => {
      requestGen.current += 1
    }
  }, [loadTrip])

  useEffect(() => {
    function onRefresh() {
      if (document.visibilityState === 'hidden') return
      window.clearTimeout(focusRefreshTimer.current)
      focusRefreshTimer.current = window.setTimeout(() => loadTrip({ silent: true }), 150)
    }
    window.addEventListener('focus', onRefresh)
    document.addEventListener('visibilitychange', onRefresh)
    return () => {
      window.removeEventListener('focus', onRefresh)
      document.removeEventListener('visibilitychange', onRefresh)
      window.clearTimeout(focusRefreshTimer.current)
    }
  }, [loadTrip])

  useEffect(() => {
    if (!trip?.id) return undefined
    if (trip.status !== 'dispatched' && trip.status !== 'in_progress') return undefined
    const timer = window.setInterval(() => loadTrip({ silent: true }), 8_000)
    return () => window.clearInterval(timer)
  }, [trip?.id, trip?.status, loadTrip])

  useEffect(() => {
    if (!trip) return
    const { dispatches } = trip
    const next = nextOpenDispatchId(dispatches)
    const prevStatusById = prevStatusByIdRef.current

    setExpandedId((prev) => {
      if (!prev) return next
      const current = dispatches.find((d) => d.id === prev)
      if (!current) return next
      const wasOpen = prevStatusById[prev] !== undefined && isDispatchOpen(prevStatusById[prev])
      if (wasOpen && !isDispatchOpen(current.status)) return next
      return prev
    })

    prevStatusByIdRef.current = Object.fromEntries(dispatches.map((d) => [d.id, d.status]))
    // Only re-sync when dispatch id/status signature changes (not every silent poll with same statuses).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trip read intentionally when signature changes
  }, [dispatchStatusSignature])

  useEffect(() => {
    setShowAllMaterials(false)
  }, [expandedId])

  useEffect(() => {
    const hasQr = Object.keys(qrByDispatch).length > 0
    if (!hasQr) return undefined
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [qrByDispatch])

  const canComplete = useMemo(() => trip?.status === 'in_progress', [trip])
  const progress = useMemo(() => (trip ? tripProgress(trip.dispatches) : { done: 0, total: 0 }), [trip])
  const hasOpen = useMemo(
    () => (trip ? trip.dispatches.some((d) => isDispatchOpen(d.status)) : false),
    [trip],
  )
  const busy = isLoading || isRefreshing

  async function handleStart() {
    if (!trip) return
    setIsStarting(true)
    setActionError(null)
    try {
      await startTrip(trip.id)
      setToast(delmanTerms.trip.startSuccess)
      loadTrip({ silent: true })
    } catch (e) {
      setActionError(e instanceof Error ? e.message : delmanTerms.trip.startFailed)
    } finally {
      setIsStarting(false)
    }
  }

  async function handleComplete() {
    if (!trip) return
    setIsCompleting(true)
    setActionError(null)
    try {
      await completeTrip(trip.id)
      setToast(delmanTerms.trip.completeSuccess)
      setConfirmCompleteOpen(false)
      loadTrip({ silent: true })
    } catch (e) {
      setActionError(e instanceof Error ? e.message : delmanTerms.trip.completeFailed)
    } finally {
      setIsCompleting(false)
    }
  }

  async function handleGenerateQr(dispatchId: string) {
    setGeneratingDispatchId(dispatchId)
    setActionError(null)
    try {
      const res = await generateDispatchQr(dispatchId)
      setQrByDispatch((prev) => ({
        ...prev,
        [dispatchId]: { token: res.token, payload: res.payload, expiresAt: res.expires_at },
      }))
      setNow(Date.now())
    } catch (e) {
      setActionError(e instanceof Error ? e.message : delmanTerms.trip.generateQrFailed)
    } finally {
      setGeneratingDispatchId(null)
    }
  }

  if (!tripId) {
    return (
      <section className="card space-y-3">
        <p className="text-body-sm text-status-danger">{delmanTerms.trip.loadError}</p>
        <Link to="/delman/trip" className="btn-outline block text-center">
          {delmanTerms.trip.back}
        </Link>
      </section>
    )
  }

  return (
    <section className="space-y-4">
      <div className="relative">
        <button
          type="button"
          className="btn-outline absolute top-0 right-0 min-w-[44px] px-3 z-10"
          onClick={() => loadTrip({ silent: false })}
          disabled={busy}
          aria-label={delmanTerms.trip.refreshAria}
          title={delmanTerms.trip.refreshAria}
        >
          <span className={`material-symbols-outlined ${busy ? 'animate-spin' : ''}`}>refresh</span>
        </button>
        <div className="pr-12">
          <button
            type="button"
            className="text-body-sm text-primary inline-flex items-center gap-1 min-h-[44px]"
            onClick={() => navigate('/delman/trip')}
          >
            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
            {delmanTerms.trip.back}
          </button>
        </div>
      </div>

      {!trip && isLoading && <SkeletonList count={2} label={delmanTerms.trip.loading} />}

      {loadError && !trip && (
        <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
          <span className="text-body-sm">{delmanTerms.trip.loadError}</span>
          <button type="button" className="btn-outline min-h-[36px] text-body-sm" onClick={() => loadTrip({ silent: false })}>
            {delmanTerms.trip.retry}
          </button>
        </div>
      )}

      {trip && (
        <>
          <div className="card space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="page-title break-words">{trip.warehouse_name}</p>
                <p className="text-body-sm text-on-surface-variant font-mono mt-1">{trip.trip_code}</p>
                <p className="text-body-sm text-on-surface-variant mt-1">
                  {delmanTerms.trip.progressDapur(progress.done, progress.total)}
                </p>
              </div>
              <span className={`shrink-0 ${tripStatusBadge(trip.status)}`}>{tripStatusLabel(trip.status)}</span>
            </div>
          </div>

          {actionError && (
            <div role="alert" className="alert-error">
              <span className="material-symbols-outlined text-base shrink-0">error</span>
              <span>{actionError}</span>
            </div>
          )}

          {trip.status === 'dispatched' && (
            <button
              type="button"
              className="btn-primary w-full min-h-[48px]"
              disabled={isStarting}
              onClick={() => void handleStart()}
            >
              {isStarting ? delmanTerms.trip.starting : delmanTerms.trip.start}
            </button>
          )}

          {trip.dispatches.length === 0 ? (
            <EmptyState icon="local_shipping" title={delmanTerms.trip.emptyTitle} description={delmanTerms.trip.emptyDescription} />
          ) : (
            <div className="space-y-3">
              {trip.dispatches.map((dispatch) => (
                <KitchenDispatchCard
                  key={dispatch.id}
                  dispatch={dispatch}
                  tripStatus={trip.status}
                  expanded={expandedId === dispatch.id}
                  showAllMaterials={showAllMaterials}
                  onToggle={() => setExpandedId((id) => (id === dispatch.id ? null : dispatch.id))}
                  onToggleMaterials={() => setShowAllMaterials((v) => !v)}
                  qr={qrByDispatch[dispatch.id]}
                  now={now}
                  onGenerateQr={handleGenerateQr}
                  isGenerating={generatingDispatchId === dispatch.id}
                  onToast={setToast}
                />
              ))}
            </div>
          )}

          {canComplete && (
            <div className="card space-y-3">
              {!confirmCompleteOpen ? (
                <button type="button" className="btn-outline w-full min-h-[48px]" onClick={() => setConfirmCompleteOpen(true)}>
                  {delmanTerms.trip.complete}
                </button>
              ) : (
                <div className="space-y-3">
                  <p className="text-body-sm font-semibold text-on-surface">{delmanTerms.trip.completeConfirmTitle}</p>
                  <p className="text-body-sm text-on-surface-variant">
                    {hasOpen
                      ? delmanTerms.trip.completeConfirmDescriptionOpen
                      : delmanTerms.trip.completeConfirmDescriptionAllDone}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="btn-outline flex-1 min-h-[44px]"
                      onClick={() => setConfirmCompleteOpen(false)}
                    >
                      {delmanTerms.trip.completeConfirmCancel}
                    </button>
                    <button
                      type="button"
                      className="btn-primary flex-1 min-h-[44px]"
                      disabled={isCompleting}
                      onClick={() => void handleComplete()}
                    >
                      {isCompleting ? delmanTerms.trip.completing : delmanTerms.trip.completeConfirmYes}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </section>
  )
}
